import { create } from "zustand";
import { useNotepadPerSlideStore } from "./useNotepadPerSlideStore";
import { useSlideStore } from "@/features/tutor/store/useSlideStore";

export type NotepadState =
  | "CLOSED"
  | "TOAST_VISIBLE"
  | "OPEN_AI_ACTIVE"
  | "OPEN_STUDENT_REVIEW"
  | "CLOSING_COUNTDOWN"
  | "CLOSE_WARNING"
  | "INACTIVITY_WARNING"
  | "OPEN_NOTEPAD_BITE";

export type CanvasConnectionStatus =
  | "waiting"
  | "connecting"
  | "open"
  | "retrying"
  | "exhausted"
  | "session_lost";

export type IngressType = "ai" | "student" | "notepad_bite" | null;

export interface NotepadCheckpointToken {
  sessionId: string;
  slideIndex: number;
  stateVersion: number;
  snapshotRevision: number;
}

interface NotepadStore {
  // State
  state: NotepadState;
  ingressType: IngressType;
  notepadWs: WebSocket | null;
  clearPersistedState: (() => void) | null;
  flushCanvasState: (() => void) | null;

  // WebSocket
  setNotepadWs: (ws: WebSocket | null) => void;
  clearNotepadWs: (ws: WebSocket) => void;
  preprocessOwner: object | null;
  connectionStatus: CanvasConnectionStatus;
  retryConnection: (() => void) | null;
  claimConnection: (owner: object, retry: () => void) => void;
  releaseConnection: (owner: object) => void;
  setConnectionStatus: (status: CanvasConnectionStatus, owner: object) => void;
  checkpointToken: NotepadCheckpointToken | null;
  setCheckpointToken: (
    token: NotepadCheckpointToken | null,
    owner: object,
  ) => void;
  advanceCheckpointRevision: (
    token: NotepadCheckpointToken,
    revision: number,
  ) => void;

  // Persistence cleanup
  registerClearPersistedState: (cb: () => void) => void;
  unregisterClearPersistedState: () => void;

  // Pre-switch flush
  registerFlushCanvasState: (cb: () => void) => void;
  unregisterFlushCanvasState: () => void;

  // State transitions
  showToast: () => void;
  openFromToast: () => void;
  openForReview: () => void;
  openForNotepadBite: () => void;
  dismissToast: () => void;
  startCloseCountdown: () => void;
  cancelCloseCountdown: () => void;
  showCloseWarning: () => void;
  dismissCloseWarning: () => void;
  showInactivityWarning: () => void;
  dismissInactivityWarning: () => void;
  /** `{ flush: false }` discards the board instead of persisting it (restart). */
  close: (options?: { flush?: boolean }) => void;

  // Notepad bite mode
  isNotepadBite: boolean;

  // Connection-level preprocessing (gateway CANVAS_SEED / CANVAS_READY).
  // The opening "start teaching" call is gated on preprocessing completion.
  gatewayReady: boolean;
  pendingSeedActions: Record<string, unknown>[] | null;
  seedApplied: boolean;
  preprocessGateSettled: boolean;
  setGatewayReady: (owner?: object | null) => void;
  markPreprocessGateSettled: (owner?: object | null) => void;
  setPendingSeedActions: (
    actions: Record<string, unknown>[],
    owner?: object | null,
  ) => void;
  markSeedApplied: (owner?: object | null) => void;
  resetPreprocess: (owner?: object | null) => void;

  // Canvas "back to content" visibility — set by CanvasOutOfViewTracker inside tldraw
  isContentOutOfView: boolean;
  setContentOutOfView: (v: boolean) => void;
  onBackToContent: (() => void) | null;
  registerBackToContent: (cb: () => void) => void;
  unregisterBackToContent: () => void;

  // Multi-page state
  pages: number[]; // page indices for current slide
  currentPageIndex: number; // which page is active (-1 = none)
  isLimitedMode: boolean; // true = once per slide, false = unlimited

  // Multi-page actions
  setLimitedMode: (v: boolean) => void;
  addPage: () => number; // appends next index, returns it
  switchToPage: (pageIndex: number) => void;
  resetPages: () => void; // clear in-memory pages (not sessionStorage)
  restorePages: (indices: number[]) => void; // bulk restore from sessionStorage index
}

// Selector: replaces old isCanvasOpen boolean
export const selectIsNotepadOpen = (s: NotepadStore) =>
  s.state !== "CLOSED" && s.state !== "TOAST_VISIBLE";

// Selector: true when in notepad bite mode (sole teaching agent, no close button)
export const selectIsNotepadBite = (s: NotepadStore) => s.isNotepadBite;

// Selector: connection-level preprocessing is complete — the gateway signalled
// CANVAS_READY and any seed it sent has been applied to the canvas. The opening
// "start teaching" call waits on this (with a fail-open timeout for old gateways).
export const selectPreprocessComplete = (s: NotepadStore) =>
  s.gatewayReady && (s.pendingSeedActions === null || s.seedApplied);

// The opening turn's gate fails open after PREPROCESS_GATE_TIMEOUT_MS so an old
// or unreachable gateway degrades the lesson rather than bricking it. A typed
// turn has to honour the same escape hatch: gating it on CANVAS_READY alone
// leaves chat permanently dead wherever that chunk never arrives.
export const selectTypedTurnAdmitted = (s: NotepadStore) =>
  selectPreprocessComplete(s) || s.preprocessGateSettled;

// Selector: AI is active only on the latest page during AI ingress (or notepad bite)
export const selectIsAIActiveOnCurrentPage = (s: NotepadStore) =>
  (s.ingressType === "ai" || s.ingressType === "notepad_bite") &&
  s.pages.length > 0 &&
  s.currentPageIndex === Math.max(...s.pages);

/** @deprecated Use selectIsAIActiveOnCurrentPage */
export const selectIsCurrentPageInteractive = selectIsAIActiveOnCurrentPage;

// Selector: true when the notepad is open but the AI should be suppressed
// (student-review mode OR AI-ingress viewing a historical page).
// Used by BottomBar, useSpeakButton, and Screen to grey out mic / block recording.
export const selectIsNotepadNonInteractive = (s: NotepadStore) => {
  if (s.state === "OPEN_STUDENT_REVIEW") return true;
  // AI-ingress on a non-latest page: suppress AI interaction
  if (
    (s.ingressType === "ai" || s.ingressType === "notepad_bite") &&
    s.pages.length > 1 &&
    s.currentPageIndex !== Math.max(...s.pages) &&
    s.state !== "CLOSED" &&
    s.state !== "TOAST_VISIBLE"
  ) {
    return true;
  }
  return false;
};

export const useNotepadStore = create<NotepadStore>((set, get) => ({
  state: "CLOSED",
  ingressType: null,
  notepadWs: null,
  clearPersistedState: null,
  flushCanvasState: null,
  isNotepadBite: false,
  gatewayReady: false,
  pendingSeedActions: null,
  seedApplied: false,
  preprocessGateSettled: false,
  preprocessOwner: null,
  connectionStatus: "waiting",
  retryConnection: null,
  checkpointToken: null,
  claimConnection: (owner, retry) =>
    set({
      preprocessOwner: owner,
      notepadWs: null,
      retryConnection: retry,
      connectionStatus: "waiting",
      gatewayReady: false,
      pendingSeedActions: null,
      seedApplied: false,
      preprocessGateSettled: false,
      checkpointToken: null,
    }),
  releaseConnection: (owner) => {
    if (get().preprocessOwner !== owner) return;
    get().resetPreprocess(owner);
    set({
      preprocessOwner: null,
      retryConnection: null,
      connectionStatus: "waiting",
    });
  },
  setConnectionStatus: (connectionStatus, owner) => {
    if (get().preprocessOwner === owner) set({ connectionStatus });
  },
  setCheckpointToken: (checkpointToken, owner) => {
    if (get().preprocessOwner === owner) set({ checkpointToken });
  },
  advanceCheckpointRevision: (token, revision) => {
    if (
      get().checkpointToken === token &&
      Number.isInteger(revision) &&
      revision > token.snapshotRevision
    ) {
      set({ checkpointToken: { ...token, snapshotRevision: revision } });
    }
  },
  setGatewayReady: (owner) => {
    if (owner === undefined || get().preprocessOwner === owner)
      set({ gatewayReady: true });
  },
  markPreprocessGateSettled: (owner) => {
    if (owner === undefined || get().preprocessOwner === owner)
      set({ preprocessGateSettled: true });
  },
  setPendingSeedActions: (actions, owner) => {
    if (owner === undefined || get().preprocessOwner === owner)
      set({ pendingSeedActions: actions, seedApplied: false });
  },
  markSeedApplied: (owner) => {
    if (owner === undefined || get().preprocessOwner === owner)
      set({ seedApplied: true });
  },
  resetPreprocess: (owner) => {
    if (owner !== undefined && get().preprocessOwner !== owner) return;
    set({
      gatewayReady: false,
      pendingSeedActions: null,
      seedApplied: false,
      preprocessGateSettled: false,
      checkpointToken: null,
    });
  },
  isContentOutOfView: false,
  setContentOutOfView: (v) => set({ isContentOutOfView: v }),
  onBackToContent: null,
  registerBackToContent: (cb) => set({ onBackToContent: cb }),
  unregisterBackToContent: () => set({ onBackToContent: null }),

  setNotepadWs: (ws) => set({ notepadWs: ws }),
  clearNotepadWs: (ws) => {
    if (get().notepadWs === ws) set({ notepadWs: null });
  },

  registerClearPersistedState: (cb) => set({ clearPersistedState: cb }),
  unregisterClearPersistedState: () => set({ clearPersistedState: null }),

  registerFlushCanvasState: (cb) => set({ flushCanvasState: cb }),
  unregisterFlushCanvasState: () => set({ flushCanvasState: null }),

  // Multi-page initial state
  pages: [],
  currentPageIndex: -1,
  isLimitedMode: false, // default to unlimited; overridden by backend canvas_access response

  setLimitedMode: (v) => set({ isLimitedMode: v }),

  addPage: () => {
    const { pages } = get();
    const nextIndex = pages.length > 0 ? Math.max(...pages) + 1 : 0;
    set({ pages: [...pages, nextIndex], currentPageIndex: nextIndex });
    return nextIndex;
  },

  switchToPage: (pageIndex) => {
    const { flushCanvasState, currentPageIndex } = get();
    if (pageIndex !== currentPageIndex && flushCanvasState) {
      flushCanvasState();
    }
    set({ currentPageIndex: pageIndex });
  },

  resetPages: () => set({ pages: [], currentPageIndex: -1 }),

  restorePages: (indices) => {
    if (indices.length === 0) return;
    const latest = Math.max(...indices);
    set({ pages: indices, currentPageIndex: latest });
  },

  showToast: () => {
    if (get().state !== "CLOSED") return;
    set({ state: "TOAST_VISIBLE", ingressType: "ai" });
  },

  openFromToast: () => {
    if (get().state !== "TOAST_VISIBLE") return;
    set({ state: "OPEN_AI_ACTIVE" });
    useNotepadPerSlideStore
      .getState()
      .markSlideUsed(useSlideStore.getState().currentSlideIndex);
  },

  openForReview: () => {
    if (get().state !== "CLOSED") return;
    set({
      state: "OPEN_STUDENT_REVIEW",
      ingressType: "student",
    });
  },

  openForNotepadBite: () => {
    set({
      state: "OPEN_NOTEPAD_BITE",
      ingressType: "notepad_bite",
      isNotepadBite: true,
    });
  },

  dismissToast: () => {
    if (get().state !== "TOAST_VISIBLE") return;
    set({ state: "CLOSED", ingressType: null });
  },

  startCloseCountdown: () => {
    if (get().state !== "OPEN_AI_ACTIVE") return;
    set({ state: "CLOSING_COUNTDOWN" });
  },

  cancelCloseCountdown: () => {
    if (get().state !== "CLOSING_COUNTDOWN") return;
    set({ state: "OPEN_AI_ACTIVE" });
  },

  showCloseWarning: () => {
    if (get().state !== "OPEN_AI_ACTIVE") return;
    set({ state: "CLOSE_WARNING" });
  },

  dismissCloseWarning: () => {
    if (get().state !== "CLOSE_WARNING") return;
    set({ state: "OPEN_AI_ACTIVE" });
  },

  showInactivityWarning: () => {
    if (get().state !== "OPEN_STUDENT_REVIEW") return;
    set({ state: "INACTIVITY_WARNING" });
  },

  dismissInactivityWarning: () => {
    if (get().state !== "INACTIVITY_WARNING") return;
    set({ state: "OPEN_STUDENT_REVIEW" });
  },

  /**
   * Unconditional: valid from any state.
   *
   * Flushes the board to sessionStorage by default, so an ordinary close keeps
   * the student's work. Pass `{ flush: false }` when the work is being thrown
   * away — restart bite / restart lesson.
   *
   * That option exists because the restart paths delete the saved board and
   * THEN closed the notepad, and the flush inside this call promptly wrote the
   * board back under the same key. (The key is stable across a restart:
   * `compute_session_id` is derived from user + lesson, and the URL's
   * `sessionId` is never rewritten.) So the restart cleared storage, restored
   * it a line later, and the canvas reloaded the work the student had just
   * asked to discard. Making the intent explicit here is why this is an option
   * rather than a call-ordering rule four sites have to remember.
   */
  close: (options) => {
    const { flushCanvasState } = get();
    if (flushCanvasState && options?.flush !== false) flushCanvasState();
    set({
      state: "CLOSED",
      ingressType: null,
      preprocessOwner: null,
      retryConnection: null,
      connectionStatus: "waiting",
      isNotepadBite: false,
      isContentOutOfView: false,
      onBackToContent: null,
      gatewayReady: false,
      pendingSeedActions: null,
      seedApplied: false,
      preprocessGateSettled: false,
    });
  },
}));
